// @flow

import React, { Component } from 'react';
import Button from 'antd/lib/button';
import Popconfirm from 'antd/lib/popconfirm';
import notification from 'antd/lib/notification';
import qs from 'query-string';
import { Link } from '@reach/router';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';

import type {
  Osago,
  ListState,
  UserAccess,
  Vehicle,
  Contractor
} from './../../../lib/types';
import { Section } from './../../../components/layout';
import { Table, Icon, ButtonsRow } from './../../../components/ui';
import { notificationLoading } from './../../../components/Notifications';
import Header from './../../../components/layout/Header';
import {
  getListInitialState,
  setQueryParams,
  convertVehicleToString,
  formatDateTimeToString,
  convertContractorToString,
  applyMaskToValue,
  toLocalStringRu,
  navigate
} from './../../../lib/helpers';
import { osagoApi, type FetchListParams } from './../../../lib/api';
import { withUserAccess } from './../../withUserAccess';

import OsagoFilter from './Filter';
import type { OsagoFilterParams } from './Filter';
import { formatOsagoMask } from '../../../components/masked-inputs/OsagoInput';
import { canHandleService } from '../lib';
import type { PersistFilterPayload } from '../../../ducks/persistFilters';
import type { AppState } from '../../../ducks/redux';
import { setFilter } from '../../../ducks/persistFilters';

type Props = {
  location: Location & { state: { page: number } },
  userAccess: UserAccess[],
  persistFilters: any,
  setFilter: (payload: PersistFilterPayload) => void
};
type State = ListState<Osago> & {
  filterPath: string
};

export const COLUMNS = [
  {
    title: 'Марка',
    sorter: true,
    width: 400,
    breakByWidth: true,
    dataIndex: 'vehicle',
    render: (vehicle: Vehicle) => convertVehicleToString(vehicle)
  },
  {
    title: 'Серия и номер ОСАГО',
    dataIndex: 'osagoNumber',
    width: 200,
    breakByWidth: true,
    render: (osago: string) => applyMaskToValue(osago, formatOsagoMask)
  },
  {
    title: 'Дата окончания',
    width: 150,
    breakByWidth: true,
    dataIndex: 'osagoFinishedDate',
    render: (value: string) => formatDateTimeToString(value, 'DD.MM.YYYY')
  },
  {
    title: 'Наименование страховщика',
    sorter: true,
    width: 300,
    breakByWidth: true,
    dataIndex: 'contractor',
    render: (contractor: Contractor) => convertContractorToString(contractor)
  },
  {
    title: 'Серия и номер диагностической карты',
    width: 220,
    breakByWidth: true,
    dataIndex: 'diagnosticCardNumber'
  },
  {
    title: 'Сумма оплаты в руб. без НДС',
    dataIndex: 'paymentAmount',
    width: 200,
    breakByWidth: true,
    render: (paymentAmount: number) => toLocalStringRu(paymentAmount || 0)
  }
];

export class List extends Component<Props, State> {
  static defaultProps = {
    location: {}
  };

  state = {
    ...getListInitialState(),
    filterPath: window.location.pathname
  };

  columns = [...COLUMNS];

  async componentDidMount() {
    const { page, ...filter } = qs.parse(window.location.search);
    if (!isEmpty(filter)) {
      await this.setPersistFilter(filter);
    }
    if (this.canAdd()) {
      this.columns.push({
        stopPropagation: true,
        width: 20,
        dataIndex: 'id',
        render: (id: number) => (
          <Popconfirm
            title="Вы уверены, что хотите удалить запись?"
            onConfirm={async () => this.deleteOsago(id)}
          >
            <Icon type="x" />
          </Popconfirm>
        )
      });
    }
    await this.fetchOsagoList(page);
  }

  deleteOsago = async (id: number) => {
    try {
      notificationLoading({
        message: 'Удаление...',
        key: 'deleting'
      });
      await osagoApi.deleteOsago(id);
      await this.fetchOsagoList(this.state.page);
    } catch (error) {
      notification.error({
        message: 'Ошибка',
        description: error.message
      });
    } finally {
      notification.close('deleting');
    }
  };

  fetchOsagoList = async (page: number = 1, params: FetchListParams<> = {}) => {
    const { filterPath } = this.state;
    const filter = this.props.persistFilters[filterPath] || {};
    this.setState({ loading: true });
    const { data, totalCount } = await osagoApi.fetchOsagos({
      ...filter,
      page,
      ...params
    });
    setQueryParams({ page });
    this.setState({ loading: false, data, totalCount, page });
  };

  handleRowClick = (id: number) => navigate(`/services/osagos/${id}`);

  canAdd = () => canHandleService(this.props.userAccess);

  setPersistFilter = async (values: OsagoFilterParams) => {
    const { filterPath } = this.state;
    await this.props.setFilter({
      path: filterPath,
      values
    });
  };

  applyFilter = async (filter: OsagoFilterParams) => {
    await this.setPersistFilter(filter);
    await this.fetchOsagoList();
  };

  cleanFilter = async () => {
    await this.setPersistFilter({});
    await this.fetchOsagoList();
  };

  handlePrint = async () => {
    try {
      const filter = this.props.persistFilters[this.state.filterPath];
      notificationLoading({
        message: 'Загрузка файла отчета...',
        key: 'print'
      });
      await osagoApi.printReport(filter);
    } catch (error) {
      notification.error({
        message: 'Ошибка',
        description: error.message
      });
    } finally {
      notification.close('print');
    }
  };

  render() {
    const { location } = this.props;
    const {
      data,
      totalCount,
      pageSize,
      page,
      loading,
      filterPath
    } = this.state;
    const filter = this.props.persistFilters[filterPath];
    return (
      <>
        <Header
          left={<h1>Полисы ОСАГО</h1>}
          right={
            <ButtonsRow>
              <Button onClick={this.handlePrint}>Печать</Button>
              {this.canAdd() && (
                <Link to="/services/osagos/new">
                  <Button type="primary" data-cy="add">
                    Создать
                  </Button>
                </Link>
              )}
            </ButtonsRow>
          }
        />
        <Section>
          <OsagoFilter
            filter={filter}
            applyFilter={this.applyFilter}
            cleanFilter={this.cleanFilter}
          />
          <Table
            onRow={record => ({
              onClick: () => this.handleRowClick(record.id)
            })}
            fetch={this.fetchOsagoList}
            pagination={{
              page,
              pageSize,
              totalCount,
              location
            }}
            bordered
            scroll={{
              x: 'auto',
              y: 'calc(100vh - 270px)'
            }}
            loading={loading}
            columns={this.columns}
            data={data.map(item => ({
              ...item,
              key: item.id
            }))}
          />
        </Section>
      </>
    );
  }
}

export default connect(
  (state: AppState) => ({
    persistFilters: state.persistFilters
  }),
  {
    setFilter
  }
)(withUserAccess(List));
